import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { getLogger } from "@/infrastructure/di";
import { resolveInvalidationKeys, type InvalidationEntity } from "./entityDependencies";

interface CreateMutationConfig<TData, TError, TVariables, TContext> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidates?: { entities: InvalidationEntity[]; orgId: string };
  invalidateKeys?: QueryKey[] | ((data: TData, variables: TVariables) => QueryKey[]);
  /** Optimistic-update hook: runs before mutationFn; its return value is the
   * rollback context handed to onError/onSettled. */
  onMutate?: (variables: TVariables) => Promise<TContext | undefined> | TContext | undefined;
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void;
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: TContext | undefined,
  ) => void;
  errorLogMessage?: string;
}

/**
 * Hook factory that wraps useMutation with standardised invalidation and logging.
 * Internal to application/queries — not exported from the barrel.
 */
export function useCreateMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(config: CreateMutationConfig<TData, TError, TVariables, TContext>) {
  const queryClient = useQueryClient();

  return useMutation<TData, TError, TVariables, TContext | undefined>({
    mutationFn: config.mutationFn,
    onMutate: config.onMutate,
    onSuccess: (data, variables, context) => {
      if (config.invalidates) {
        const keys = resolveInvalidationKeys(config.invalidates.entities, config.invalidates.orgId);
        for (const key of keys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      if (config.invalidateKeys) {
        const keys =
          typeof config.invalidateKeys === "function"
            ? config.invalidateKeys(data, variables)
            : config.invalidateKeys;
        for (const key of keys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      config.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      getLogger().error(config.errorLogMessage ?? "Mutation failed:", error);
      config.onError?.(error, variables, context);
    },
    onSettled: config.onSettled,
  });
}
